// client/src/pages/wholesaler/TeamManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Users, UserPlus, Shield, Clock, MoreVertical,
  Mail, Trash2, UserCog, Ban, CheckCircle
} from 'lucide-react';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import ConfirmModal from '../../components/common/ConfirmModal';
import UpgradePromptCard from '../../components/common/UpgradePromptCard';
import InviteTeamMemberModal from '../../components/team/InviteTeamMemberModal';
import AssignAccountsModal from '../../components/team/AssignAccountsModal';
import { wholesalerAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const SUB_ROLE_COLORS = {
  manager: 'bg-blue-100 text-blue-700',
  office: 'bg-green-100 text-green-700',
  sales_rep: 'bg-purple-100 text-purple-700'
};

const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-amber-100 text-amber-700',
  suspended: 'bg-red-100 text-red-700'
};

const TeamManagement = () => {
  const { t } = useTranslation('wholesaler');
  const { user } = useAuth();
  const navigate = useNavigate();

  // Manager-only page — redirect other sub-roles to their dashboard
  const subRole = user?.teamContext?.subRole || 'manager';
  useEffect(() => {
    if (subRole !== 'manager') {
      navigate('/wholesaler/dashboard', { replace: true });
    }
  }, [subRole, navigate]);

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierInfo, setTierInfo] = useState({ tier: 'starter', memberCount: 0, memberLimit: 1, isUnlimited: false });
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [assignMember, setAssignMember] = useState(null);

  const fetchTeam = useCallback(async () => {
    try {
      const res = await wholesalerAPI.getTeam();
      const data = res.data.data;
      setMembers(data.members);
      setTierInfo({
        tier: data.tier,
        memberCount: data.memberCount,
        memberLimit: data.memberLimit,
        isUnlimited: data.isUnlimited
      });
    } catch (error) {
      if (error.response?.status === 403) {
        // Tier gated or not manager
      } else {
        toast.error('Failed to load team');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // Tier gate: Starter
  if (!loading && tierInfo.tier === 'starter') {
    return (
      <UpgradePromptCard
        badge={t('team.upgradeCard.badge', 'Professional & Enterprise')}
        title={t('team.tierGate')}
        body={t('team.tierGateDesc')}
        ctaLabel={t('team.upgrade')}
        backLabel={t('team.upgradeCard.back', 'Back to dashboard')}
      />
    );
  }

  const handleUpdateMember = async (memberId, data) => {
    try {
      await wholesalerAPI.updateTeamMember(memberId, data);
      toast.success(t('team.actions.updateSuccess', 'Member updated'));
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Update failed');
    }
    setOpenMenuId(null);
  };

  const handleRemoveMember = async (memberId) => {
    try {
      await wholesalerAPI.removeTeamMember(memberId);
      toast.success(t('team.actions.removeSuccess', 'Member removed'));
      fetchTeam();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Remove failed');
    }
  };

  const getInitial = (email) => (email || '?')[0].toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="pt-16 lg:pt-0">
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Users size={24} />
                {t('team.title')}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {tierInfo.isUnlimited
                  ? t('team.memberCountUnlimited', { count: tierInfo.memberCount })
                  : t('team.memberCount', { count: tierInfo.memberCount, limit: tierInfo.memberLimit })
                }
              </p>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm font-medium"
            >
              <UserPlus size={16} />
              {t('team.inviteMember')}
            </button>
          </div>

          {/* Loading */}
          {loading && (
            <div className="text-center py-20">
              <div className="animate-spin h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto" />
            </div>
          )}

          {/* Empty state */}
          {!loading && members.length <= 1 && (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
              <UserPlus size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">{t('team.emptyState')}</p>
            </div>
          )}

          {/* Member list */}
          {!loading && members.length > 1 && (
            <div className="grid gap-4">
              {members.map((member) => (
                <div
                  key={member._id}
                  className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 flex items-center gap-4 hover:shadow-sm transition-shadow"
                >
                  {/* Avatar */}
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                    member.subRole === 'manager' ? 'bg-blue-100 text-blue-700' :
                    member.subRole === 'office' ? 'bg-green-100 text-green-700' :
                    'bg-purple-100 text-purple-700'
                  }`}>
                    {getInitial(member.email)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900 truncate">{member.email}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${SUB_ROLE_COLORS[member.subRole]}`}>
                        {t(`team.subRoles.${member.subRole}`)}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[member.status]}`}>
                        {t(`team.status.${member.status}`)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {member.joinedAt
                        ? `${t('team.joined')} ${new Date(member.joinedAt).toLocaleDateString()}`
                        : member.status === 'pending'
                          ? t('team.status.pending')
                          : ''
                      }
                    </p>
                  </div>

                  {/* Actions (not for manager) */}
                  {member.subRole !== 'manager' && (
                    <div className="relative">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === member._id ? null : member._id)}
                        className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                      >
                        <MoreVertical size={16} />
                      </button>

                      {openMenuId === member._id && (
                        <div className="absolute right-0 top-10 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                          {/* Toggle role */}
                          {member.status === 'active' && (
                            <button
                              onClick={() => handleUpdateMember(member._id, {
                                subRole: member.subRole === 'office' ? 'sales_rep' : 'office'
                              })}
                              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <UserCog size={14} />
                              {t('team.actions.editRole')}
                            </button>
                          )}

                          {/* Suspend / Activate */}
                          {member.status === 'active' ? (
                            <button
                              onClick={() => handleUpdateMember(member._id, { status: 'suspended' })}
                              className="w-full text-left px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 flex items-center gap-2"
                            >
                              <Ban size={14} />
                              {t('team.actions.suspend')}
                            </button>
                          ) : member.status === 'suspended' ? (
                            <button
                              onClick={() => handleUpdateMember(member._id, { status: 'active' })}
                              className="w-full text-left px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50 flex items-center gap-2"
                            >
                              <CheckCircle size={14} />
                              {t('team.actions.activate')}
                            </button>
                          ) : null}

                          {/* Assign Accounts (sales_rep only) */}
                          {member.subRole === 'sales_rep' && member.status === 'active' && (
                            <button
                              onClick={() => { setOpenMenuId(null); setAssignMember(member); }}
                              className="w-full text-left px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 flex items-center gap-2"
                            >
                              <Users size={14} />
                              {t('team.actions.assignAccounts', 'Assign Accounts')}
                            </button>
                          )}

                          {/* Resend invite (pending only) */}
                          {member.status === 'pending' && (
                            <button
                              onClick={() => {
                                wholesalerAPI.inviteTeamMember({ email: member.email, subRole: member.subRole })
                                  .then(() => { toast.success(t('team.actions.resendSuccess', 'Invite resent')); setOpenMenuId(null); })
                                  .catch(() => toast.error('Resend failed'));
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
                            >
                              <Mail size={14} />
                              {t('team.actions.resendInvite')}
                            </button>
                          )}

                          {/* Remove */}
                          <button
                            onClick={() => {
                              setOpenMenuId(null);
                              setConfirmAction({
                                memberId: member._id,
                                email: member.email
                              });
                            }}
                            className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                          >
                            <Trash2 size={14} />
                            {t('team.actions.remove')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>

      {/* Invite Modal */}
      <InviteTeamMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onInvited={fetchTeam}
        memberCount={tierInfo.memberCount}
        memberLimit={tierInfo.memberLimit}
        isUnlimited={tierInfo.isUnlimited}
      />

      {/* Assign Accounts Modal */}
      <AssignAccountsModal
        isOpen={!!assignMember}
        onClose={() => setAssignMember(null)}
        member={assignMember}
        onAssigned={fetchTeam}
      />

      {/* Remove Confirmation */}
      <ConfirmModal
        isOpen={!!confirmAction}
        onCancel={() => setConfirmAction(null)}
        onConfirm={() => {
          handleRemoveMember(confirmAction.memberId);
          setConfirmAction(null);
        }}
        title={t('team.confirmRemoveTitle', 'Remove Team Member')}
        message={t('team.confirmRemove', { email: confirmAction?.email || '' })}
        confirmLabel={t('team.actions.remove')}
        cancelLabel={t('common:cancel', 'Cancel')}
        confirmColor="red"
        icon="warning"
      />

      {/* Close dropdown on outside click */}
      {openMenuId && (
        <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
      )}

      <Footer />
    </div>
  );
};

export default TeamManagement;
